import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Trophy,
  Users,
  Loader2,
  AlertCircle,
  Check,
  Save
} from 'lucide-react';
import { useApi } from '../../contexts/ApiContext';

const raceTypeOrder = {
  'Feature': 1, 'A-Main': 1, 'A-Feature': 1, 'Main Event': 1,
  'Last Chance Showdown': 2, 'C - Z Main': 3,
  'Dash': 4, 'Heat Race': 5,
  'Qualifier': 6, 'Non-Qualifier': 7,
  'Qualifying': 8, 'Hot Laps': 9, 'Loop Test': 10,
};

const raceTypeBadgeColors = {
  'Feature': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'A-Main': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Last Chance Showdown': 'bg-purple-100 text-purple-700 border-purple-200',
  'Heat Race': 'bg-blue-100 text-blue-700 border-blue-200',
  'Qualifying': 'bg-amber-100 text-amber-700 border-amber-200',
  'Dash': 'bg-pink-100 text-pink-700 border-pink-200',
  'Hot Laps': 'bg-slate-100 text-slate-600 border-slate-200',
  'Loop Test': 'bg-slate-100 text-slate-600 border-slate-200',
};

function getPositionStyle(pos) {
  if (pos === 1) return 'bg-amber-400 text-amber-900';
  if (pos === 2) return 'bg-slate-300 text-slate-800';
  if (pos === 3) return 'bg-amber-600 text-white';
  return 'bg-slate-100 text-slate-600';
}

function HealthDot({ results }) {
  if (!results || results.length === 0) return <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />;
  const total = results.length;
  const filled = results.filter(r => r.finish_position != null).length;
  if (filled === total) return <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />;
  if (filled === 0) return <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />;
  return <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />;
}

function EditableCell({ value, onChange, numeric = false, className = '', placeholder = '', onKeyDown }) {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    const val = e.target.value;
    if (numeric) {
      if (val === '' || val === '-') { onChange(null); return; }
      const num = Number(val);
      if (!isNaN(num)) onChange(num);
    } else {
      onChange(val || null);
    }
  };
  return (
    <input
      ref={inputRef}
      type="text"
      inputMode={numeric ? 'numeric' : 'text'}
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className={`w-full bg-transparent border-0 border-b border-transparent hover:border-slate-300 focus:border-wrg-blue-500 focus:ring-0 focus:outline-none px-1 py-1 text-sm transition-colors ${className}`}
    />
  );
}

function ResultsTable({ race, api }) {
  const [results, setResults] = useState([]);
  const [saveStatus, setSaveStatus] = useState('idle');
  const saveTimerRef = useRef(null);
  const tableRef = useRef(null);

  const { isLoading } = useQuery({
    queryKey: ['race-results', race.race_ID],
    queryFn: async () => {
      const response = await api.results.getByRace(race.race_ID);
      if (response.success && response.data?.results) {
        setResults(response.data.results);
        return response.data.results;
      }
      return [];
    },
    enabled: !!race.race_ID,
  });

  const debouncedSave = useCallback((updatedResults) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('pending');
    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const response = await api.results.updateRace(race.race_ID, { results: updatedResults });
        setSaveStatus(response.success ? 'saved' : 'error');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    }, 1000);
  }, [race.race_ID, api.results]);

  const handleFieldChange = (competitorId, field, value) => {
    const updated = results.map(r =>
      r.competitor_ID === competitorId ? { ...r, [field]: value } : r
    );
    setResults(updated);
    debouncedSave(updated);
  };

  const handleKeyDown = (e, rowIdx, colIdx) => {
    const editableColumns = ['car_number', 'finish_position', 'points', 'winnings', 'status'];
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextRow = tableRef.current?.querySelector(`[data-row="${rowIdx + 1}"][data-col="${colIdx}"] input, [data-row="${rowIdx + 1}"][data-col="${colIdx}"] select`);
      nextRow?.focus();
    } else if (e.key === 'Tab' && !e.shiftKey) {
      const nextCol = tableRef.current?.querySelector(`[data-row="${rowIdx}"][data-col="${colIdx + 1}"] input, [data-row="${rowIdx}"][data-col="${colIdx + 1}"] select`);
      if (nextCol) {
        e.preventDefault();
        nextCol.focus();
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      const prevCol = tableRef.current?.querySelector(`[data-row="${rowIdx}"][data-col="${colIdx - 1}"] input, [data-row="${rowIdx}"][data-col="${colIdx - 1}"] select`);
      if (prevCol) {
        e.preventDefault();
        prevCol.focus();
      }
    }
  };

  const isFeature = ['Feature', 'A-Main', 'A-Feature', 'Main Event'].includes(race.race_type);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <Trophy className="w-10 h-10 mb-3 opacity-50" />
        <p className="text-sm">No results for this race</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div className="text-sm text-slate-500">
          {results.length} competitors
        </div>
        <div className="flex items-center gap-2 text-sm">
          {saveStatus === 'pending' && <span className="text-slate-400">Unsaved changes...</span>}
          {saveStatus === 'saving' && <span className="text-amber-600 flex items-center gap-1"><Save className="w-3.5 h-3.5 animate-pulse" /> Saving...</span>}
          {saveStatus === 'saved' && <span className="text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Saved</span>}
          {saveStatus === 'error' && <span className="text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Save failed</span>}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto" ref={tableRef}>
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2 text-left w-14">Pos</th>
              <th className="px-3 py-2 text-center w-16">Start</th>
              <th className="px-3 py-2 text-left w-20">Car</th>
              <th className="px-3 py-2 text-left">Driver</th>
              <th className="px-3 py-2 text-center w-16">Laps</th>
              <th className="px-3 py-2 text-center w-16">Behind</th>
              <th className="px-3 py-2 text-center w-20">Best Lap</th>
              <th className="px-3 py-2 text-center w-16">BL#</th>
              <th className="px-3 py-2 text-center w-20">BL Speed</th>
              <th className="px-3 py-2 text-center w-20">Avg Speed</th>
              <th className="px-3 py-2 text-center w-20">Total Time</th>
              <th className="px-3 py-2 text-center w-20">Diff Time</th>
              <th className="px-3 py-2 text-center w-20">Points</th>
              <th className="px-3 py-2 text-right w-24">Winnings</th>
              <th className="px-3 py-2 text-center w-20">Status</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result, rowIdx) => {
              const pos = result.finish_position || result.race_position || rowIdx + 1;
              return (
                <tr key={result.competitor_ID} className={`border-b border-slate-100 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={0}>
                    <EditableCell
                      value={result.finish_position}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'finish_position', v)}
                      numeric
                      placeholder="#"
                      className="w-12 text-center font-bold"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 0)}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-center text-sm text-slate-500">
                    {result.start_position || <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={1}>
                    <EditableCell
                      value={result.car_number}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'car_number', v)}
                      placeholder="#"
                      className="w-16 font-mono font-bold text-center"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 1)}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="text-sm font-medium text-slate-800">{result.driver_name}</div>
                    {result.team_name && <div className="text-xs text-slate-400">{result.team_name}</div>}
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={2}>
                    <EditableCell
                      value={result.num_laps}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'num_laps', v)}
                      numeric
                      placeholder="-"
                      className="w-12 text-center"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 2)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={3}>
                    <EditableCell
                      value={result.diff_laps}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'diff_laps', v)}
                      numeric
                      placeholder="-"
                      className="w-12 text-center"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 3)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={4}>
                    <EditableCell
                      value={result.best_lap_time}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'best_lap_time', v)}
                      placeholder="-"
                      className="w-16 text-center font-mono text-xs"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 4)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={5}>
                    <EditableCell
                      value={result.best_lap_number}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'best_lap_number', v)}
                      numeric
                      placeholder="-"
                      className="w-12 text-center"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 5)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={6}>
                    <EditableCell
                      value={result.best_lap_speed_mph}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'best_lap_speed_mph', v)}
                      numeric
                      placeholder="-"
                      className="w-16 text-center"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 6)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={7}>
                    <EditableCell
                      value={result.average_speed_mph}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'average_speed_mph', v)}
                      numeric
                      placeholder="-"
                      className="w-16 text-center"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 7)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={8}>
                    <EditableCell
                      value={result.total_time}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'total_time', v)}
                      placeholder="-"
                      className="w-16 text-center font-mono text-xs"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 8)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={9}>
                    <EditableCell
                      value={result.diff_time}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'diff_time', v)}
                      placeholder="-"
                      className="w-16 text-center font-mono text-xs"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 9)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={10}>
                    <EditableCell
                      value={result.points}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'points', v)}
                      numeric
                      placeholder="-"
                      className="w-16 text-center"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 10)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={11}>
                    <EditableCell
                      value={result.winnings}
                      onChange={(v) => handleFieldChange(result.competitor_ID, 'winnings', v)}
                      numeric
                      placeholder="-"
                      className="w-20 text-right"
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 11)}
                    />
                  </td>
                  <td className="px-3 py-1.5" data-row={rowIdx} data-col={12}>
                    <select
                      value={result.status || ''}
                      onChange={(e) => handleFieldChange(result.competitor_ID, 'status', e.target.value || null)}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 12)}
                      className="w-full bg-transparent border-0 text-sm text-slate-600 focus:ring-0 focus:outline-none cursor-pointer py-1"
                    >
                      <option value="">-</option>
                      <option value="dnf">DNF</option>
                      <option value="dns">DNS</option>
                      <option value="dq">DQ</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function EventResults() {
  const { seriesSlug, eventId } = useParams();
  const api = useApi();
  const [selectedRaceId, setSelectedRaceId] = useState(null);
  const [racesWithResults, setRacesWithResults] = useState({});

  const { data: eventAndRaces, isLoading, error: queryError } = useQuery({
    queryKey: ['event-results', eventId],
    queryFn: async () => {
      const eventResult = await api.events.getById(eventId);
      if (!eventResult.success) throw new Error(eventResult.message || 'Failed to load event');

      let races = [];
      const ooeResponse = await api.apiCall(`/api/events/${eventId}/ooe`);
      if (Array.isArray(ooeResponse)) {
        const raceComponents = ooeResponse.filter(c => c.race_type_ID);
        races = raceComponents.map(c => ({
          race_ID: c.race_ID || c.id,
          race_name: c.title,
          race_type: c.race_type_name,
          total_laps: c.total_laps,
          entry_count: 0,
        }));
      }

      if (races.length === 0) {
        const racesResponse = await api.events.getRaces(eventId);
        if (racesResponse.success && Array.isArray(racesResponse.data)) {
          races = racesResponse.data
            .map(r => ({
              race_ID: r.race_ID,
              race_name: r.race_name,
              race_type: r.race_type,
              total_laps: r.total_laps || r.laps_to_run,
              entry_count: r.entry_count || 0,
            }))
            .sort((a, b) => (raceTypeOrder[a.race_type] || 99) - (raceTypeOrder[b.race_type] || 99));
        }
      }

      return { event: eventResult.data, races };
    },
    enabled: !!eventId,
  });

  const event = eventAndRaces?.event ?? null;
  const races = eventAndRaces?.races ?? [];
  const error = queryError?.message ?? null;

  // Auto-select first race (Feature)
  useEffect(() => {
    if (races.length > 0 && !selectedRaceId) {
      setSelectedRaceId(races[0].race_ID);
    }
  }, [races, selectedRaceId]);

  // Fetch results counts for race list health dots
  useEffect(() => {
    async function fetchCounts() {
      const counts = {};
      for (const race of races) {
        if (!race.race_ID) continue;
        const resp = await api.results.getByRace(race.race_ID);
        if (resp.success && resp.data?.results) {
          counts[race.race_ID] = resp.data.results;
        }
      }
      setRacesWithResults(counts);
    }
    if (races.length > 0) fetchCounts();
  }, [races]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRace = races.find(r => r.race_ID === selectedRaceId);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Error Loading Results</h2>
        <p className="text-slate-500 mb-4">{error}</p>
        <Link to={`/${seriesSlug}/events/${eventId}`} className="text-wrg-blue-600 hover:underline">
          Back to Event
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in flex flex-col" style={{ height: 'calc(100vh - 72px)' }}>
      {/* Compact Header */}
      <div className="flex items-center justify-between px-1 py-3 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to={`/${seriesSlug}/events/${eventId}`}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <Trophy className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-800 truncate">{event?.name}</h1>
            <p className="text-xs text-slate-500 truncate">
              {formatDate(event?.event_date)}
              {event?.track_name && ` \u00b7 ${event.track_name}`}
              {event?.track_city && `, ${event.track_city}`}
              {event?.state_code && `, ${event.state_code}`}
            </p>
          </div>
        </div>
      </div>

      {/* Split View */}
      <div className="flex flex-1 min-h-0 border-t border-slate-200">
        {/* Left Panel - Race List */}
        <div className="w-56 flex-shrink-0 border-r border-slate-200 overflow-y-auto bg-slate-50">
          <div className="p-2">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 py-2">
              Races ({races.length})
            </div>
            <div className="space-y-0.5">
              {races.map(race => {
                const isActive = race.race_ID === selectedRaceId;
                const badgeColor = raceTypeBadgeColors[race.race_type] || 'bg-slate-100 text-slate-600 border-slate-200';
                const raceResults = racesWithResults[race.race_ID];
                const entryCount = raceResults?.length || race.entry_count || 0;
                return (
                  <button
                    key={race.race_ID}
                    onClick={() => setSelectedRaceId(race.race_ID)}
                    className={`w-full text-left px-2.5 py-2 rounded-md transition-all ${
                      isActive
                        ? 'bg-white shadow-sm border border-slate-200'
                        : 'hover:bg-white/60 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`px-1.5 py-0.5 rounded text-2xs font-medium border ${badgeColor}`}>
                        {race.race_type}
                      </span>
                      <HealthDot results={raceResults} />
                    </div>
                    <div className="text-sm font-medium text-slate-700 truncate">
                      {race.race_name || race.race_type}
                    </div>
                    <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <Users className="w-3 h-3" />
                      {entryCount}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Panel - Results Table */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {selectedRace ? (
            <ResultsTable
              key={selectedRace.race_ID}
              race={selectedRace}
              api={api}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400">
              <p className="text-sm">Select a race from the list</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
